import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import PPErrorDisplay from "../components/layout/PPErrorDisplay"
import PPMatchingMasterCard from "../components/matching/PPMatchingMasterCard"
import PPMatchingCandidateCard from "../components/matching/PPMatchingCandidateCard"
import PPMatchingStatusBanner from "../components/matching/PPMatchingStatusBanner"
import PPMatchingToolbar from "../components/matching/PPMatchingToolbar"
import PPMatchingWorkspaceSkeleton from "../components/matching/PPMatchingWorkspaceSkeleton"
import PPMatchingStatsSidebar from "../components/matching/PPMatchingStatsSidebar"
import PPMatchingBulkConfirmModal from "../components/matching/PPMatchingBulkConfirmModal"
import {
    MUTATION_APPROVE,
    MUTATION_BULK_APPROVE,
    MUTATION_REJECT_ALL,
    QUERY_AUTOMATCHING_APPROVAL_AT_POSITION,
    QUERY_AUTOMATCHING_MATCH_TYPES,
    QUERY_AUTOMATCHING_PRODUCERS,
    QUERY_AUTOMATCHING_SHOPS
} from "../utils/matching/matchingQueries"
import { buildFilterInput, filterCandidatesByMinScore, LEGACY_ALGORITHM_MATCH_TYPE } from "../utils/matching/matchingUtils"
import PPUtils from "../utils/PPUtils"
import "../components/matching/matching.css"

const DIRECTION = "CUSTOMER_IS_MASTER"
const DEFAULT_BULK_MIN_SCORE = 80
const STATS_MIN_SPLIT_WIDTH = 1040

export default function PPMatching() {
    const [t] = useTranslation()

    const [shopId, setShopId] = useState(null)
    const [producerId, setProducerId] = useState(null)
    const [minScore, setMinScore] = useState(0)
    const [matchType, setMatchType] = useState("ALL")
    const [position, setPosition] = useState(0)
    const [jumpInput, setJumpInput] = useState("1")
    const [bulkMinScore, setBulkMinScore] = useState(DEFAULT_BULK_MIN_SCORE)
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)
    const [bulkApproving, setBulkApproving] = useState(false)
    const [approvingMatchId, setApprovingMatchId] = useState(null)
    const [rejectingAll, setRejectingAll] = useState(false)
    const [filtersExpanded, setFiltersExpanded] = useState(false)
    const [sessionStats, setSessionStats] = useState({ approved: 0, rejected: 0 })
    const [statsRefreshKey, setStatsRefreshKey] = useState(0)
    const [wideStatsOpen, setWideStatsOpen] = useState(true)
    const [narrowView, setNarrowView] = useState("matching")
    const [isNarrowLayout, setIsNarrowLayout] = useState(false)
    const pageBodyRef = useRef(null)
    const prevNarrowLayoutRef = useRef(false)
    const narrowViewRef = useRef("matching")

    const handleShopChange = useCallback((id) => {
        if (!PPUtils.isPositiveIntId(id)) {
            return
        }
        setShopId(id)
    }, [])

    const handleProducerChange = useCallback((id) => {
        if (id == null) {
            setProducerId(null)
            return
        }
        if (!PPUtils.isPositiveIntId(id)) {
            return
        }
        setProducerId(id)
    }, [])

    const handleMinScoreChange = useCallback((event) => {
        const next = Number(event.target.value)
        if (!Number.isFinite(next) || next < 0) {
            setMinScore(0)
            return
        }
        setMinScore(next)
    }, [])

    const handleMatchTypeChange = useCallback((value) => {
        if (typeof value !== "string" || value === "") {
            setMatchType("ALL")
            return
        }
        setMatchType(value)
    }, [])

    const handleBulkMinScoreChange = useCallback((event) => {
        const next = Number(event.target.value)
        if (!Number.isFinite(next) || next < 0) {
            setBulkMinScore(0)
            return
        }
        setBulkMinScore(next)
    }, [])

    const filter = useMemo(
        () => buildFilterInput(shopId, producerId, minScore, matchType),
        [shopId, producerId, minScore, matchType]
    )

    const bulkFilter = useMemo(
        () => buildFilterInput(shopId, producerId, 0, matchType),
        [shopId, producerId, matchType]
    )

    const producerListFilter = useMemo(
        () => buildFilterInput(shopId, null, 0, "ALL"),
        [shopId]
    )

    const matchTypeListFilter = useMemo(
        () => buildFilterInput(shopId, null, 0, "ALL"),
        [shopId]
    )

    const filterReady = PPUtils.isPositiveIntId(shopId)
    const hasCustomer = PPUtils.isValidCustomerId(PPUtils.getEffectiveCustomerId())

    const {
        data: shopsData,
        loading: shopsLoading,
        error: shopsError,
        refetch: refetchShops
    } = useQuery(QUERY_AUTOMATCHING_SHOPS, {
        skip: !hasCustomer
    })

    const {
        data: producersData,
        loading: producersLoading
    } = useQuery(QUERY_AUTOMATCHING_PRODUCERS, {
        variables: { filter: producerListFilter },
        skip: !filterReady
    })

    const {
        data: matchTypesData,
        loading: matchTypesLoading
    } = useQuery(QUERY_AUTOMATCHING_MATCH_TYPES, {
        variables: { filter: matchTypeListFilter },
        skip: !filterReady
    })

    const [loadApproval, { data: approvalData, loading: approvalLoading, error: approvalError }] =
        useLazyQuery(QUERY_AUTOMATCHING_APPROVAL_AT_POSITION, {
            fetchPolicy: "network-only"
        })

    const [approveMatch] = useMutation(MUTATION_APPROVE)
    const [bulkApprove] = useMutation(MUTATION_BULK_APPROVE)
    const [rejectAll] = useMutation(MUTATION_REJECT_ALL)

    const approvalResult = approvalData?.automatchingApprovalAtPosition
    const approval = approvalResult?.approval
    const loadStatus = approvalResult?.status
    const actualPosition = approvalResult?.actualPosition ?? position
    const totalCount = approvalResult?.totalCount ?? 0

    const visibleCandidates = useMemo(
        () => filterCandidatesByMinScore(approval?.candidates, minScore),
        [approval?.candidates, minScore]
    )

    useEffect(() => {
        const shops = shopsData?.automatchingShops
        if (shops?.length && shopId === null) {
            const withMatches = shops.find((s) => s.remainingMatches > 0)
            const nextShop = withMatches ?? shops[0]
            if (PPUtils.isPositiveIntId(nextShop?.id)) {
                setShopId(nextShop.id)
            }
        }
    }, [shopsData, shopId])

    const refreshQueueAndApproval = useCallback(
        (pos) => {
            if (!filterReady) {
                return
            }
            loadApproval({ variables: { filter, position: pos } })
        },
        [filter, filterReady, loadApproval]
    )

    const refreshAfterMatchAction = useCallback(
        (pos) => {
            refetchShops()
            refreshQueueAndApproval(pos)
        },
        [refetchShops, refreshQueueAndApproval]
    )

    useEffect(() => {
        if (!filterReady) {
            return
        }
        setPosition(0)
        setJumpInput("1")
    }, [shopId, producerId, minScore, matchType, filterReady])

    useEffect(() => {
        if (!filterReady) {
            return
        }
        refreshQueueAndApproval(position)
    }, [filterReady, position, filter, refreshQueueAndApproval])

    useEffect(() => {
        if (actualPosition !== undefined && actualPosition !== null) {
            setJumpInput(String(actualPosition + 1))
        }
    }, [actualPosition])

    const shopOptions = useMemo(
        () =>
            (shopsData?.automatchingShops ?? []).map((s) => ({
                value: s.id,
                label: `${s.name} ${s.lang.toUpperCase()} (${s.remainingMatches})`
            })),
        [shopsData]
    )

    const producerOptions = useMemo(
        () =>
            (producersData?.automatchingProducers ?? [])
                .filter((p) => p.id > 0 && p.name?.trim() && p.count > 0)
                .map((p) => ({
                    value: p.id,
                    label: `${p.name} (${p.count})`
                })),
        [producersData]
    )

    useEffect(() => {
        if (producerId && !producerOptions.some((o) => o.value === producerId)) {
            setProducerId(null)
        }
    }, [producerId, producerOptions])

    const selectedShop = shopOptions.find((o) => o.value === shopId) ?? null
    const selectedShopRecord = shopsData?.automatchingShops?.find((s) => s.id === shopId)
    const selectedShopName = selectedShopRecord
        ? `${selectedShopRecord.name} ${selectedShopRecord.lang.toUpperCase()}`
        : selectedShop?.label?.split(" (")[0] ?? ""
    const selectedProducer = producerOptions.find((o) => o.value === producerId) ?? null

    const matchSourceOptions = useMemo(() => {
        const options = [{ value: "ALL", label: t("matching.source_all") }]
        for (const entry of matchTypesData?.automatchingMatchTypes ?? []) {
            const label =
                entry.matchType === LEGACY_ALGORITHM_MATCH_TYPE
                    ? t("matching.source_algorithm")
                    : entry.label || entry.matchType
            options.push({
                value: entry.matchType,
                label: `${label} (${entry.count})`
            })
        }
        return options
    }, [matchTypesData, t])

    const selectedMatchSource = matchSourceOptions.find((o) => o.value === matchType) ?? matchSourceOptions[0]

    useEffect(() => {
        if (matchType === "ALL") {
            return
        }
        const stillValid = matchSourceOptions.some((option) => option.value === matchType)
        if (!stillValid) {
            setMatchType("ALL")
        }
    }, [matchSourceOptions, matchType])

    const handleApprove = async (matchId) => {
        setApprovingMatchId(matchId)
        try {
            const result = await approveMatch({
                variables: { matchId, direction: DIRECTION }
            })
            const response = result.data?.approveAutomatchingMatch
            if (response?.success) {
                setSessionStats((s) => ({ ...s, approved: s.approved + 1 }))
                toast.success(t("matching.approve_success"))
                refreshAfterMatchAction(position)
                setStatsRefreshKey((k) => k + 1)
            } else {
                toast.error(t("matching.approve_failed"))
            }
        } catch (e) {
            toast.error(e.message)
        } finally {
            setApprovingMatchId(null)
        }
    }

    const handleRejectAll = async () => {
        if (!approval?.master?.productNr || !approval?.shopId) {
            return
        }
        setRejectingAll(true)
        try {
            const result = await rejectAll({
                variables: {
                    direction: DIRECTION,
                    shopId: approval.shopId,
                    masterProductNr: approval.master.productNr
                }
            })
            if (result.data?.rejectAllAutomatchingMatches?.success) {
                setSessionStats((s) => ({ ...s, rejected: s.rejected + 1 }))
                refreshAfterMatchAction(position)
                setStatsRefreshKey((k) => k + 1)
            }
        } catch (e) {
            toast.error(e.message)
        } finally {
            setRejectingAll(false)
        }
    }

    const handlePrev = () => {
        if (position > 0) {
            setPosition(position - 1)
        }
    }

    const handleNext = () => {
        if (position + 1 < totalCount) {
            setPosition(position + 1)
        }
    }

    const handleFirst = () => {
        if (totalCount > 0) {
            setPosition(0)
        }
    }

    const handleLast = () => {
        if (totalCount > 0) {
            setPosition(totalCount - 1)
        }
    }

    const handleJumpInputChange = useCallback((e) => {
        setJumpInput(e.target.value.replace(/\D/g, ""))
    }, [])

    const handleJump = useCallback(
        (e) => {
            e.preventDefault()
            if (totalCount <= 0) {
                return
            }
            const raw = jumpInput.trim()
            if (!raw) {
                return
            }
            const target = parseInt(raw, 10)
            if (Number.isNaN(target) || target < 1) {
                setPosition(0)
                setJumpInput("1")
                return
            }
            const positionIndex = Math.min(totalCount - 1, target - 1)
            setPosition(positionIndex)
        },
        [jumpInput, totalCount]
    )

    const handleBulkApproveRequest = () => {
        if (!filterReady || totalCount <= 0) {
            return
        }
        setBulkConfirmOpen(true)
    }

    const handleBulkApproveConfirm = async () => {
        if (!filterReady) {
            return
        }
        setBulkApproving(true)
        try {
            const result = await bulkApprove({
                variables: { filter: bulkFilter, minScore: bulkMinScore }
            })
            const response = result.data?.bulkApproveAutomatchingMatches
            if (response?.success) {
                if (response.approvedCount > 0) {
                    setSessionStats((s) => ({ ...s, approved: s.approved + response.approvedCount }))
                    toast.success(t("matching.bulk_success", { count: response.approvedCount }))
                } else {
                    toast.info(t("matching.bulk_none_matched", { min: bulkMinScore }))
                }
                setPosition(0)
                setJumpInput("1")
                refreshAfterMatchAction(0)
                setStatsRefreshKey((k) => k + 1)
                setBulkConfirmOpen(false)
            } else {
                toast.error(t("matching.approve_failed"))
            }
        } catch (e) {
            toast.error(e.message)
        } finally {
            setBulkApproving(false)
        }
    }

    const openFilters = useCallback(() => {
        const el = document.getElementById("collapseMatchingFilter")
        if (el) {
            el.classList.add("show")
            setFiltersExpanded(true)
        }
    }, [])

    useEffect(() => {
        narrowViewRef.current = narrowView
    }, [narrowView])

    useEffect(() => {
        const el = pageBodyRef.current
        if (!el) {
            return undefined
        }

        const updateLayout = (width) => {
            const narrow = width < STATS_MIN_SPLIT_WIDTH
            const wasNarrow = prevNarrowLayoutRef.current

            if (!wasNarrow && narrow) {
                setNarrowView("matching")
            }

            if (wasNarrow && !narrow && narrowViewRef.current === "stats") {
                setWideStatsOpen(true)
            }

            prevNarrowLayoutRef.current = narrow
            setIsNarrowLayout(narrow)
        }

        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? el.getBoundingClientRect().width
            updateLayout(width)
        })
        observer.observe(el)
        updateLayout(el.getBoundingClientRect().width)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const collapseEl = document.getElementById("collapseMatchingFilter")
        if (!collapseEl) return undefined
        const onShown = () => setFiltersExpanded(true)
        const onHidden = () => setFiltersExpanded(false)
        collapseEl.addEventListener("shown.bs.collapse", onShown)
        collapseEl.addEventListener("hidden.bs.collapse", onHidden)
        return () => {
            collapseEl.removeEventListener("shown.bs.collapse", onShown)
            collapseEl.removeEventListener("hidden.bs.collapse", onHidden)
        }
    }, [])

    const shopsWithMatches = useMemo(
        () => (shopsData?.automatchingShops ?? []).filter((s) => s.remainingMatches > 0),
        [shopsData]
    )

    const isLoading = shopsLoading || (filterReady && approvalLoading)
    const isWorkspaceLoading = filterReady && approvalLoading
    const noMoreMatches = filterReady && !isLoading && (loadStatus === "NO_MORE_MATCHES" || totalCount === 0)
    const displayPosition = totalCount > 0 ? actualPosition + 1 : 0
    const hasShops = shopOptions.length > 0
    const shopsLoaded = !shopsLoading && shopsData !== undefined
    const showApprovalContent = filterReady && approval && !noMoreMatches && !isWorkspaceLoading
    const showWorkspaceSkeleton = filterReady && isWorkspaceLoading && !noMoreMatches && hasShops
    const workspaceRevealKey = `${shopId ?? "none"}-${actualPosition}-${approval?.master?.productNr ?? "empty"}`

    let bannerVariant = null
    if (!isLoading && shopsLoaded && !hasShops) {
        bannerVariant = "no_shops"
    } else if (!isLoading && hasShops && noMoreMatches) {
        bannerVariant = "empty_queue"
    }

    const showMatching = !isNarrowLayout || narrowView === "matching"
    const showStats =
        (isNarrowLayout && narrowView === "stats") || (!isNarrowLayout && wideStatsOpen)
    const isSplitLayout = !isNarrowLayout && wideStatsOpen

    if (!hasCustomer) {
        return (
            <div className="card card-outline card-primary">
                <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
                    <h3 className="card-title mb-0">{t("matching.title")}</h3>
                </div>
                <div className="card-body">
                    <PPMatchingStatusBanner variant="no_customer" />
                </div>
            </div>
        )
    }

    if (shopsError) {
        return <PPErrorDisplay error={shopsError} />
    }

    return (
        <div className="card card-outline card-primary">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
                <h3 className="card-title mb-0">{t("matching.title")}</h3>
                <span className="text-muted small">{t("matching.session_stats", sessionStats)}</span>
            </div>
            <div className="card-body">
                <div
                    className={
                        "pp-matching-page" +
                        (isSplitLayout ? " pp-matching-page--split" : " pp-matching-page--main-only") +
                        (isNarrowLayout ? " pp-matching-page--narrow" : "") +
                        (isNarrowLayout && narrowView === "stats" ? " pp-matching-page--narrow-stats" : "")
                    }
                >
                    <div className="pp-matching-page__body" ref={pageBodyRef}>
                        {showMatching && (
                        <div className="pp-matching-page__main">
                            {isNarrowLayout && (
                                <div className="pp-matching-view-switch">
                                    <button
                                        type="button"
                                        className="pp-matching-view-switch__btn btn btn-outline-primary btn-sm"
                                        onClick={() => setNarrowView("stats")}
                                    >
                                        <i className="fas fa-chart-bar" aria-hidden="true" />
                                        <span>{t("matching.stats.show")}</span>
                                        <i className="fas fa-chevron-right pp-matching-view-switch__arrow" aria-hidden="true" />
                                    </button>
                                </div>
                            )}

                            <PPMatchingToolbar
                                shopOptions={shopOptions}
                                selectedShop={selectedShop}
                                onShopChange={handleShopChange}
                                shopsLoading={shopsLoading}
                                displayPosition={displayPosition}
                                totalCount={totalCount}
                                position={position}
                                jumpInput={jumpInput}
                                onJumpInputChange={handleJumpInputChange}
                                onJump={handleJump}
                                onFirst={handleFirst}
                                onPrev={handlePrev}
                                onNext={handleNext}
                                onLast={handleLast}
                                onBulkApprove={handleBulkApproveRequest}
                                filtersExpanded={filtersExpanded}
                                isLoading={isLoading}
                                noMoreMatches={noMoreMatches || !hasShops}
                                approvingMatchId={approvingMatchId}
                                bulkApproving={bulkApproving}
                                bulkMinScore={bulkMinScore}
                                onBulkMinScoreChange={handleBulkMinScoreChange}
                                showBulkActions={filterReady && totalCount > 0}
                                producerOptions={producerOptions}
                                selectedProducer={selectedProducer}
                                onProducerChange={handleProducerChange}
                                producersLoading={producersLoading}
                                matchTypesLoading={matchTypesLoading}
                                filterReady={filterReady}
                                minScore={minScore}
                                onMinScoreChange={handleMinScoreChange}
                                matchSourceOptions={matchSourceOptions}
                                selectedMatchSource={selectedMatchSource}
                                onMatchSourceChange={handleMatchTypeChange}
                            />

                            {approvalError && <PPErrorDisplay error={approvalError} />}

                            <div className="pp-matching-workspace">
                                {bannerVariant && (
                                    <PPMatchingStatusBanner
                                        variant={bannerVariant}
                                        selectedShop={selectedShop}
                                        shopsWithMatches={shopsWithMatches}
                                        onShopChange={handleShopChange}
                                        onOpenFilters={openFilters}
                                    />
                                )}

                                {showWorkspaceSkeleton && <PPMatchingWorkspaceSkeleton candidateCount={3} />}

                                {showApprovalContent && (
                                    <div className="pp-matching-workspace__content" key={workspaceRevealKey}>
                                        <div
                                            className="pp-matching-reveal"
                                            style={{ "--reveal-delay": "0ms" }}
                                        >
                                            <PPMatchingMasterCard
                                                master={approval.master}
                                                priceLang={selectedShopRecord?.lang}
                                                onRejectAllCandidates={handleRejectAll}
                                                rejecting={rejectingAll}
                                            />
                                        </div>
                                        <div className="pp-matching-candidate-list">
                                            {visibleCandidates.map((candidate, index) => (
                                                <div
                                                    key={candidate.matchId}
                                                    className="pp-matching-reveal"
                                                    style={{ "--reveal-delay": `${80 + index * 70}ms` }}
                                                >
                                                    <PPMatchingCandidateCard
                                                        candidate={candidate}
                                                        onApprove={handleApprove}
                                                        approving={approvingMatchId === candidate.matchId}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {!isLoading && !showApprovalContent && !showWorkspaceSkeleton && (
                                    <div className="pp-matching-workspace__placeholder" aria-hidden="true">
                                        <div className="pp-matching-workspace__placeholder-master" />
                                        <div className="pp-matching-workspace__placeholder-candidate" />
                                        <div className="pp-matching-workspace__placeholder-candidate" />
                                    </div>
                                )}
                            </div>
                        </div>
                        )}

                        {showStats && (
                            <PPMatchingStatsSidebar
                                shopId={shopId}
                                displayPosition={displayPosition}
                                totalCount={totalCount}
                                statsRefreshKey={statsRefreshKey}
                                isNarrowLayout={isNarrowLayout}
                                onCollapse={() =>
                                    isNarrowLayout ? setNarrowView("matching") : setWideStatsOpen(false)
                                }
                            />
                        )}

                        {!wideStatsOpen && !isNarrowLayout && (
                            <button
                                type="button"
                                className="pp-matching-stats-expand btn btn-outline-primary btn-sm"
                                onClick={() => setWideStatsOpen(true)}
                                title={t("matching.stats.show")}
                            >
                                <span>{t("matching.stats.show")}</span>
                                <i className="fas fa-chevron-left pp-matching-stats-expand__arrow" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <PPMatchingBulkConfirmModal
                open={bulkConfirmOpen}
                shopName={selectedShopName}
                minScore={bulkMinScore}
                openCount={totalCount}
                loading={bulkApproving}
                onConfirm={handleBulkApproveConfirm}
                onCancel={() => !bulkApproving && setBulkConfirmOpen(false)}
            />
        </div>
    )
}
